import { forwardRef, useRef, useEffect, useImperativeHandle, useState } from 'react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import './quill-table.css'
import './quill-extensions.css'
import { initChecklistModule } from './quill-checkbox'
import { initInlineSelectModule, insertInlineSelect } from './quill-inline-select'
import type { RichTextareaProps } from './types'
import { cnMerge } from '../../utils/twmerge'

export const RichTextarea = forwardRef<HTMLDivElement, RichTextareaProps>(
  (
    {
      className,
      placeholder = 'Descreva detalhadamente a demanda...',
      value = '',
      onChange,
      disabled = false,
      label,
      description,
      error,
      toolbar = true,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const quillRef = useRef<Quill | null>(null)
    const isMountedRef = useRef(false)
    
    // Estado para modal de inserir select
    const [showSelectModal, setShowSelectModal] = useState(false)
    const [selectOptions, setSelectOptions] = useState('')
    const [selectPlaceholder, setSelectPlaceholder] = useState('Selecione...')

    useImperativeHandle(ref, () => containerRef.current!, [])

    useEffect(() => {
      if (!containerRef.current || isMountedRef.current) {
        return
      }

      const container = containerRef.current

      // Remove qualquer elemento Quill existente
      const existingToolbars = container.querySelectorAll('.ql-toolbar')
      const existingContainers = container.querySelectorAll('.ql-container')
      
      existingToolbars.forEach(el => el.remove())
      existingContainers.forEach(el => el.remove())
      container.innerHTML = ''

      // Configuração da toolbar com checklist e select
      const toolbarOptions = toolbar
        ? [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            ['link'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }, 'checklist'],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['insertSelect'],
            ['clean'],
          ]
        : false

      // Registrar formatos de tabela no clipboard
      const Table = Quill.import('formats/table') as any
      const TableCell = Quill.import('formats/table-cell') as any
      const TableRow = Quill.import('formats/table-row') as any
      
      if (Table) Quill.register(Table, true)
      if (TableCell) Quill.register(TableCell, true)
      if (TableRow) Quill.register(TableRow, true)

      // Aguarda um tick para garantir limpeza completa
      const initTimeout = setTimeout(() => {
        try {
          const quill = new Quill(container, {
            theme: 'snow',
            placeholder,
            readOnly: disabled,
            modules: {
              toolbar: toolbarOptions,
              history: {
                delay: 1000,
                maxStack: 100,
                userOnly: true,
              },
            },
          })

          quillRef.current = quill
          isMountedRef.current = true

          // Inicializar módulos customizados
          initChecklistModule(quill, () => {
            const html = quill.root.innerHTML
            onChange?.(html === '<p><br></p>' ? '' : html)
          })
          
          initInlineSelectModule(quill, () => {
            const html = quill.root.innerHTML
            onChange?.(html === '<p><br></p>' ? '' : html)
          })

          // Handler para o botão de inserir select
          const toolbarModule = quill.getModule('toolbar')
          if (toolbarModule && typeof toolbarModule === 'object' && 'addHandler' in toolbarModule) {
            const tb = toolbarModule as { addHandler: (name: string, handler: () => void) => void }
            tb.addHandler('insertSelect', () => {
              setShowSelectModal(true)
            })
          }

          // Definir conteúdo inicial
          if (value) {
            quill.clipboard.dangerouslyPasteHTML(value)
          }

          // Listener para mudanças
          const handleTextChange = () => {
            const html = quill.root.innerHTML
            onChange?.(html === '<p><br></p>' ? '' : html)
          }

          quill.on('text-change', handleTextChange)
        } catch (error) {
          console.error('Erro ao inicializar Quill:', error)
        }
      }, 50)

      // Cleanup principal
      return () => {
        clearTimeout(initTimeout)
        
        if (quillRef.current) {
          try {
            quillRef.current.off('text-change')
            quillRef.current = null
          } catch (error) {
            console.warn('Erro no cleanup do Quill:', error)
          }
        }
        
        // Remove elementos do DOM
        const toolbars = container.querySelectorAll('.ql-toolbar')
        const containers = container.querySelectorAll('.ql-container')
        
        toolbars.forEach(el => el.remove())
        containers.forEach(el => el.remove())
        
        isMountedRef.current = false
      }
    }, []) // Dependências vazias - inicializa apenas uma vez

    // Effect separado para atualizar valor
    useEffect(() => {
      if (quillRef.current && isMountedRef.current) {
        const currentContent = quillRef.current.root.innerHTML
        const newContent = value || '<p><br></p>'
        
        if (currentContent !== newContent) {
          quillRef.current.clipboard.dangerouslyPasteHTML(newContent)
        }
      }
    }, [value])

    // Effect separado para atualizar estado disabled
    useEffect(() => {
      if (quillRef.current && isMountedRef.current) {
        quillRef.current.enable(!disabled)
      }
    }, [disabled])

    // Função para inserir o select
    const handleInsertSelect = () => {
      if (quillRef.current && selectOptions.trim()) {
        const options = selectOptions.split('\n').filter(opt => opt.trim())
        insertInlineSelect(quillRef.current, options, selectPlaceholder || undefined)
        setShowSelectModal(false)
        setSelectOptions('')
        setSelectPlaceholder('Selecione...')
        
        // Disparar onChange
        const html = quillRef.current.root.innerHTML
        onChange?.(html === '<p><br></p>' ? '' : html)
      }
    }

    return (
      <div className={cnMerge('w-full', className)} translate="no">
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
          </label>
        )}
        
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{description}</p>
        )}

        <div className="relative">
          <div
            ref={containerRef}
            translate="no"
            data-google-translate="false"
            className={cnMerge(
              'quill-editor notranslate',
              // Base styles
              '[&_.ql-editor]:text-sm [&_.ql-editor]:leading-5 [&_.ql-editor]:min-h-[9rem]',
              // Text color - DARK MODE FIX
              '[&_.ql-editor]:text-gray-900 dark:[&_.ql-editor]:!text-gray-100',
              // Placeholder color - DARK MODE FIX
              '[&_.ql-editor.ql-blank::before]:!text-gray-500 dark:[&_.ql-editor.ql-blank::before]:!text-gray-400',
              // Toolbar styles
              '[&_.ql-toolbar]:border-gray-300 dark:[&_.ql-toolbar]:border-neutral-600',
              '[&_.ql-toolbar]:bg-white dark:[&_.ql-toolbar]:bg-neutral-800',
              '[&_.ql-toolbar]:border-t [&_.ql-toolbar]:border-l [&_.ql-toolbar]:border-r [&_.ql-toolbar]:border-b-0 [&_.ql-toolbar]:rounded-t-lg',
              // Container styles
              '[&_.ql-container]:border-gray-300 dark:[&_.ql-container]:border-neutral-600',
              '[&_.ql-container]:bg-white dark:[&_.ql-container]:bg-neutral-900',
              '[&_.ql-container]:border-l [&_.ql-container]:border-r [&_.ql-container]:border-b [&_.ql-container]:border-t-0 [&_.ql-container]:rounded-b-lg',
              // Toolbar icons
              '[&_.ql-stroke]:stroke-gray-700 dark:[&_.ql-stroke]:stroke-gray-300',
              '[&_.ql-fill]:fill-gray-700 dark:[&_.ql-fill]:fill-gray-300',
              '[&_.ql-picker-label]:text-gray-700 dark:[&_.ql-picker-label]:text-gray-300',
              // Table styles
              '[&_.ql-editor_table]:border-collapse-collapse [&_.ql-editor_table]:w-full [&_.ql-editor_table]:mb-4',
              '[&_.ql-editor_table_td]:border [&_.ql-editor_table_td]:border-gray-300 dark:[&_.ql-editor_table_td]:border-neutral-600 [&_.ql-editor_table_td]:p-2',
              '[&_.ql-editor_table_th]:border [&_.ql-editor_table_th]:border-gray-300 dark:[&_.ql-editor_table_th]:border-neutral-600 [&_.ql-editor_table_th]:p-2 [&_.ql-editor_table_th]:font-semibold [&_.ql-editor_table_th]:bg-gray-50 dark:[&_.ql-editor_table_th]:bg-neutral-800',
              // Focus
              '[&_.ql-editor:focus]:outline-none',
              // Error state
              error && '[&_.ql-toolbar]:border-red-300 dark:[&_.ql-toolbar]:border-red-600 [&_.ql-container]:border-red-300 dark:[&_.ql-container]:border-red-600',
              // Disabled state
              disabled && '[&_.ql-toolbar]:bg-gray-50 dark:[&_.ql-toolbar]:bg-neutral-900 [&_.ql-toolbar]:opacity-50',
            )}
          />
        </div>

        {/* Modal para configurar o select */}
        {showSelectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 w-96 shadow-xl border border-gray-200 dark:border-neutral-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Inserir Campo de Seleção
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Placeholder (opcional)
                  </label>
                  <input
                    type="text"
                    value={selectPlaceholder}
                    onChange={(e) => setSelectPlaceholder(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Selecione..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Opções (uma por linha)
                  </label>
                  <textarea
                    value={selectOptions}
                    onChange={(e) => setSelectOptions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 h-32 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder={"Opção 1\nOpção 2\nOpção 3"}
                  />
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  💡 Dica: Você também pode digitar diretamente no texto usando a sintaxe:<br />
                  <code className="bg-gray-100 dark:bg-neutral-700 px-1 rounded">
                    {'{select:Placeholder?Opção1|Opção2|Opção3}'}
                  </code>
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowSelectModal(false)
                    setSelectOptions('')
                    setSelectPlaceholder('Selecione...')
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleInsertSelect}
                  disabled={!selectOptions.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Inserir
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    )
  }
)

RichTextarea.displayName = 'RichTextarea'
